import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import {
  CohortEntity,
  InitiativeWithProgressOutput,
  PackageUnitEntity,
} from "../../generated/graphql";
import {
  GET_INITIATIVES_AND_REFDOCS,
  GetInitiativesAndRefdocsData,
} from "./home2.queries";

export interface InitiativeWithPackageName {
  initiative: InitiativeWithProgressOutput;
  packageName: string | undefined;
}

export interface InitiativePackageGroup {
  packageId: string;
  packageName: string;
  initiatives: InitiativeWithProgressOutput[];
  isUngrouped: boolean;
}

interface PackUnitGroup {
  pack: PackageUnitEntity & { id: string };
  units: InitiativeWithProgressOutput[];
}

export function useInitiatives() {
  const { loading, error, data, refetch } = useQuery<GetInitiativesAndRefdocsData>(
    GET_INITIATIVES_AND_REFDOCS,
    {
      fetchPolicy: "no-cache",
    }
  );

  const baseInitiatives = useMemo((): InitiativeWithProgressOutput[] => {
    if (!data?.getInitiativesForLearnerWithProgress) return [];

    let list = [...data.getInitiativesForLearnerWithProgress];

    // Sort by assignment date (most recent first)
    list.sort((a, b) => b.assignmentDate - a.assignmentDate);

    // Move last active initiative to top
    list.sort((a) => (a.isLastActiveInitiative ? -1 : 1));

    const completed = list.filter((i) => i.progress >= 100);
    const inProgress = list.filter((i) => i.progress < 100);

    return [...inProgress, ...completed];
  }, [data]);

  const { initiativePackageGroups, initiativesWithPackageName } = useMemo(() => {
    const initiatives = baseInitiatives;
    const packages = data?.getAllPackages ?? [];
    const userCohortIds = data?.getUserCohorts ?? [];

    const packageUnits: Record<string, number[]> = {};
    for (const pack of packages) {
      packageUnits[pack.id] = pack.units.map((u) => +u.id);
    }

    const groups: PackUnitGroup[] = [];

    for (const pack of packages) {
      const packCohortIds = (pack.cohorts as CohortEntity[]).map((c) => c.id);
      const userInPackCohort = packCohortIds.some((c) => userCohortIds.includes(c));
      if (!userInPackCohort) continue;

      const group: PackUnitGroup = {
        pack: pack as PackageUnitEntity & { id: string },
        units: [],
      };

      for (const initiative of initiatives) {
        let unitIdToCheck = +initiative.unit.id;
        if (initiative.unit.is_private === 1) {
          unitIdToCheck = initiative.unit.parent_unit;
        }
        if (packageUnits[pack.id]?.includes(unitIdToCheck)) {
          group.units.push(initiative);
        }
      }

      if (group.units.length === 0) continue;

      const unitSequence =
        (pack.unit_sequence as { order?: Array<{ id: number }> } | null)?.order?.map(
          (row) => row.id
        ) ?? [];
      group.units.sort((a, b) => {
        const aIndex = unitSequence.indexOf(+a.unit.id);
        const bIndex = unitSequence.indexOf(+b.unit.id);
        return aIndex - bIndex;
      });

      groups.push(group);
    }

    groups.sort((a, b) => a.pack.sort_id - b.pack.sort_id);
    const sortedGroups = groups;

    const unitIdsInPackages = new Set<number>();
    for (const group of sortedGroups) {
      for (const row of group.units) {
        const id = row.unit.is_private === 1 ? row.unit.parent_unit : +row.unit.id;
        unitIdsInPackages.add(id);
      }
    }
    const initiativesWithoutPackage = initiatives.filter((i) => {
      const idToCheck = i.unit.is_private === 1 ? i.unit.parent_unit : +i.unit.id;
      return !unitIdsInPackages.has(idToCheck);
    });

    const initiativePackageGroups: InitiativePackageGroup[] = sortedGroups.map((group) => ({
      packageId: group.pack.id,
      packageName: group.pack.name,
      initiatives: group.units,
      isUngrouped: false,
    }));

    if (initiativesWithoutPackage.length > 0) {
      initiativePackageGroups.push({
        packageId: "0",
        packageName: "",
        initiatives: initiativesWithoutPackage,
        isUngrouped: true,
      });
    }

    const initiativesWithPackageName: InitiativeWithPackageName[] = [];
    for (const group of initiativePackageGroups) {
      for (const initiative of group.initiatives) {
        initiativesWithPackageName.push({
          initiative,
          packageName: group.isUngrouped ? undefined : group.packageName,
        });
      }
    }

    return { initiativePackageGroups, initiativesWithPackageName };
  }, [data, baseInitiatives]);

  const initiatives = baseInitiatives;

  const refdocCountByDocumentId = useMemo((): Record<number, number> => {
    const map: Record<number, number> = {};
    if (!data?.getRefdocViewCount) return map;
    for (const row of data.getRefdocViewCount) {
      map[row.document_id] = row.count ?? 0;
    }
    return map;
  }, [data]);

  return {
    loading,
    error,
    data,
    refetch,
    initiatives,
    initiativePackageGroups,
    initiativesWithPackageName,
    referenceDocuments: data?.getReferenceDocumentsForLearner ?? [],
    refdocCountByDocumentId,
    learnerDetails: data?.getLearnerDetails,
    learnerFirstName: data?.getLearnerDetails?.first_name,
  };
}
